import { Flex, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  ButtonText,
  FormFeedback,
  Input,
  OutlineCard,
  TextArea
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faInfoCircle
} from '@fortawesome/pro-regular-svg-icons';
import { get } from 'lodash';

import {
  FormFieldOpposedReviewers,
  FormFieldRecommendedReviewers
} from '@publishing-engine/domain/types/article';

import { FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import RinggoldSearch, { RinggoldOrg } from './RinggoldSearch';
import {
  IDependentValue,
  IReviewerRecommendation
} from '@publishing-engine/domain/models';
import {
  IEnsureReviewerInfoForAuthor,
  postEnsureReviewerForAuthor
} from '../../../../../../api/queries';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

export type IReviewersFormField =
  | FormFieldRecommendedReviewers
  | FormFieldOpposedReviewers;

type IReviewersFormFieldName = IReviewersFormField['name'];

export type IReviewerRecommendationValue = {
  preference: {
    recommendedReviewers?: IDependentValue<IReviewerRecommendation[]>;
    opposedReviewers?: IDependentValue<IReviewerRecommendation[]>;
  };
};

export const reviewersTypeLabel: Record<
  IReviewersFormFieldName,
  {
    main: string;
    secondary: string;
  }
> = {
  RecommendedReviewers: {
    main: 'Recommended',
    secondary: 'recommend'
  },
  OpposedReviewers: {
    main: 'Opposed',
    secondary: 'oppose'
  }
} as const;

const DEFAULT_EMPTY_RECORD = {
  firstName: '',
  lastName: '',
  email: '',
  affiliation: '',
  reason: ''
};

const hasNumber = (value: string): boolean => /\d/.test(value);

const ReviewerRecommendation = ({
  ctxId,
  lexiconProjectId,
  draftId,
  config,
  onConflictChange,
  type = 'draft',
  skipAffiliationCheck = false
}: {
  ctxId: string;
  lexiconProjectId: string;
  draftId: string;
  config: FormFieldRecommendedReviewers | FormFieldOpposedReviewers;
  onConflictChange?: (hasConflict: boolean) => void;
  type?: 'draft' | 'submission';
  skipAffiliationCheck?: boolean;
}) => {
  const {
    formState: { errors },
    watch,
    control,
    register,
    setValue,
    clearErrors
  } = useFormContext<IReviewerRecommendationValue>();
  const {
    params: { labels },
    caseDataFields: { main: name, dependsOn },
    name: reviewTypeName
  } = config;
  const dependsOnField = `${name}.${dependsOn}` as const;
  const mainField = `${name}.value` as const;

  const exists = watch(dependsOnField);
  const error = get(errors, name);

  const { fields, append, remove } = useFieldArray({
    control,
    name: mainField
  });

  const addRow = () => {
    append(DEFAULT_EMPTY_RECORD);
  };

  if (exists && !fields.length) {
    addRow();
  }

  useEffect(() => {
    if (!exists) {
      setValue(mainField, []);
    }
  }, [exists]);

  const toast = useToast();
  const [emailConflicts, setEmailConflicts] = useState<
    Record<number, { hasConflict: boolean; message: string }>
  >({});
  const [affiliationConflicts, setAffiliationConflicts] = useState<
    Record<
      number,
      { hasConflict: boolean; type: 'error' | 'attention'; message: string }
    >
  >({});

  const isValidEmail = (email: string): boolean => {
    const normalized = email.trim().toLowerCase();
    const regex =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i;
    return regex.test(normalized);
  };

  const hasHydratedConflictsRef = useRef(false);

  const ensureReviewer = useMutation({
    mutationFn: (reviewers: IEnsureReviewerInfoForAuthor[]) =>
      postEnsureReviewerForAuthor({
        ctxId,
        projectId: lexiconProjectId,
        draftId,
        reviewers,
        type
      }),
    onSuccess: data => {
      return data;
    },
    onError: error => {
      toast({
        variant: 'error',
        title: 'Error submitting reviewers',
        description: String(error),
        duration: 5000,
        isClosable: true
      });
    }
  });

  const validateEmailCOI = (email: string, index: number) => {
    const reviewerPayload: IEnsureReviewerInfoForAuthor[] = [
      {
        reviewerEmail: email
      }
    ];
    ensureReviewer.mutate(reviewerPayload, {
      onSuccess: data => {
        const result = data.results.find(
          r => r.reviewerEmail.toLowerCase() === email.toLowerCase()
        );
        setEmailConflicts(prev => ({
          ...prev,
          [index]: {
            hasConflict: result?.status === 'invalid',
            message: result?.reason || ''
          }
        }));
      }
    });
  };

  const validateAffiliationCOI = (affiliation: string, index: number) => {
    if (skipAffiliationCheck) {
      return;
    }
    if (!affiliation.trim().length) {
      return;
    }
    const reviewerPayload: IEnsureReviewerInfoForAuthor[] = [
      {
        affiliation
      }
    ];
    ensureReviewer.mutate(reviewerPayload, {
      onSuccess: data => {
        const result = data.results.find(r => r.affiliation === affiliation);
        if (result?.status === 'invalid') {
          setAffiliationConflicts(prev => ({
            ...prev,
            [index]: {
              hasConflict: true,
              type: 'error',
              message: result.reason
            }
          }));
        } else if (
          result?.affiliation &&
          result?.percentageMatching !== undefined &&
          result?.percentageMatching >= 80 &&
          result?.percentageMatching < 100
        ) {
          setAffiliationConflicts(prev => ({
            ...prev,
            [index]: {
              hasConflict: false,
              type: 'attention',
              message:
                'Affiliation is somewhat akin to that of an author or co-author, but you can progress further.'
            }
          }));
        } else {
          setAffiliationConflicts(prev => ({
            ...prev,
            [index]: { hasConflict: false, type: 'error', message: '' }
          }));
        }
      }
    });
  };

  const handleOrganizationSelection = (
    org: Partial<RinggoldOrg>,
    index: number
  ) => {
    // update the selected org values in form
    org.name &&
      setValue(`${mainField}.${index}.affiliation`, org.name, {
        shouldValidate: true,
        shouldDirty: true
      });
    org.ringgoldId &&
      setValue(`${mainField}.${index}.ringgoldId`, org.ringgoldId, {
        shouldValidate: true,
        shouldDirty: true
      });
    if (org.country) {
      setValue(`${mainField}.${index}.country`, org.country, {
        shouldValidate: true,
        shouldDirty: true
      });
      const organisation = watch(`${mainField}.${index}.affiliation`);
      validateAffiliationCOI(`${organisation} ${org.country}`, index);
    }
    org.countryCode &&
      setValue(`${mainField}.${index}.countryCode`, org.countryCode, {
        shouldValidate: true,
        shouldDirty: true
      });
  };

  const handleRinggoldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value.trim();
    // if input value is cleared in that case reset the ringgoldId as well
    if (!value.length) {
      setValue(`${mainField}.${index}.affiliation`, '', {
        shouldValidate: true,
        shouldDirty: true
      });
      setValue(`${mainField}.${index}.ringgoldId`, '', {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };

  const reviewerLabel = reviewersTypeLabel[config.name];

  const resetReviewerConflicts = () => {
    setEmailConflicts({});
    setAffiliationConflicts({});
    onConflictChange?.(false);
  };

  useEffect(() => {
    const hasConflict =
      Object.values(emailConflicts).some(c => c.hasConflict) ||
      Object.values(affiliationConflicts).some(c => c.hasConflict);
    onConflictChange?.(hasConflict);
  }, [emailConflicts, affiliationConflicts, onConflictChange]);

  useEffect(() => {
    if (!exists || !fields.length || hasHydratedConflictsRef.current) return;

    const payload: IEnsureReviewerInfoForAuthor[] = [];

    fields.forEach((_, index) => {
      const email = watch(`${mainField}.${index}.email`);
      const affiliation = watch(`${mainField}.${index}.affiliation`);
      const country = watch(`${mainField}.${index}.country`) || '';

      if (email) payload.push({ reviewerEmail: email });
      if (affiliation && !skipAffiliationCheck)
        payload.push({ affiliation: `${affiliation} ${country}` });
    });

    if (!payload.length) return;

    ensureReviewer.mutate(payload, {
      onSuccess: data => {
        const nextEmail: typeof emailConflicts = {};
        const nextAffiliation: typeof affiliationConflicts = {};

        data.results.forEach(result => {
          fields.forEach((_, index) => {
            const email = watch(`${mainField}.${index}.email`);
            const affiliation = watch(`${mainField}.${index}.affiliation`);
            const country = watch(`${mainField}.${index}.country`) || '';

            if (
              email &&
              result.reviewerEmail?.toLowerCase() === email.toLowerCase()
            ) {
              nextEmail[index] = {
                hasConflict: result.status === 'invalid',
                message: result.reason || ''
              };
            }

            if (
              affiliation &&
              result.affiliation === `${affiliation} ${country}`
            ) {
              if (result.status === 'invalid') {
                nextAffiliation[index] = {
                  hasConflict: true,
                  type: 'error',
                  message: result.reason
                };
              } else if (
                result.percentageMatching &&
                result.percentageMatching >= 80 &&
                result.percentageMatching < 100
              ) {
                nextAffiliation[index] = {
                  hasConflict: false,
                  type: 'attention',
                  message:
                    'Affiliation is somewhat akin to that of an author or co-author, but you can progress further.'
                };
              }
            }
          });
        });

        setEmailConflicts(nextEmail);
        setAffiliationConflicts(nextAffiliation);
        hasHydratedConflictsRef.current = true;
      }
    });
  }, [exists, fields]);

  return (
    <OutlineCard width="100%" padding={4}>
      {/* ----- Data Availability ----- */}
      <Flex flexDirection="column" gap={4} width="100%">
        <FieldLabel
          label={labels.main}
          subText={labels.subtitle}
          isRequired
          helpText={labels.helpText}
        />

        <Flex flexDirection="column" gap={4}>
          <RadioGroup
            control={control}
            name={dependsOnField}
            options={[
              {
                value: true,
                children: labels.options['true']?.main
              },
              {
                value: false,
                children: labels.options['false']?.main
              }
            ]}
            onChange={value => {
              if (value === false) {
                setValue(mainField, undefined);
                resetReviewerConflicts();
              }
            }}
          />

          {error?.exists && (
            <FormFeedback variant="error">
              Please indicate whether you would like to{' '}
              {reviewerLabel.secondary} specific reviewers
            </FormFeedback>
          )}
        </Flex>

        {/* ----- Reviewer Form ----- */}
        {exists ? (
          <>
            <Text textStyle="headingItem">{reviewerLabel.main} Reviewers</Text>
            <OutlineCard borderless width="100%">
              <Flex
                flexDirection="column"
                gap={8}
                alignItems="flex-start"
                width={'100%'}
              >
                {fields.map((field, index) => {
                  const emailConflict = emailConflicts[index] || {
                    hasConflict: false,
                    message: ''
                  };
                  const affiliationConflict = affiliationConflicts[index] || {
                    hasConflict: false,
                    type: 'error',
                    message: ''
                  };

                  const country = watch(`${mainField}.${index}.country`);
                  const countryCode =
                    watch(`${mainField}.${index}.countryCode`) ?? '';
                  const ringgoldId = watch(`${mainField}.${index}.ringgoldId`);
                  const firstNameValue =
                    watch(`${mainField}.${index}.firstName`) || '';
                  const lastNameValue =
                    watch(`${mainField}.${index}.lastName`) || '';
                  const hasFirstNameNumber = hasNumber(firstNameValue);
                  const hasLastNameNumber = hasNumber(lastNameValue);

                  return (
                    <OutlineCard
                      key={field.id}
                      width="100%"
                      padding={4}
                      bgColor={'surfaceSecondaryFill'}
                    >
                      <Flex flexDirection="column" gap={4} width="100%">
                        <Flex width="100%" gap={4}>
                          {/* --- First Name --- */}
                          <Flex flexDirection="column" flex="1" gap={2}>
                            <FieldLabel
                              label="First Name"
                              subText=""
                              isRequired
                              helpText={labels.helpText}
                            />

                            <Input
                              {...register(`${mainField}.${index}.firstName`, {
                                required: 'First name is required',
                                validate: value => {
                                  if (!value?.trim()) {
                                    return 'Cannot be only spaces';
                                  }
                                  return (
                                    !hasNumber(value) ||
                                    'First name must not contain numbers'
                                  );
                                }
                              })}
                              aria-label="Reviewer first name"
                              placeholder="Enter the reviewer's first name"
                              isInvalid={Boolean(
                                error?.value?.[index]?.firstName ||
                                  hasFirstNameNumber
                              )}
                              onBlur={e => {
                                const trimmed = e.target.value.trim();
                                setValue(
                                  `${mainField}.${index}.firstName`,
                                  trimmed,
                                  {
                                    shouldValidate: true
                                  }
                                );
                              }}
                            />

                            {error?.value?.[index]?.firstName ? (
                              <FormFeedback variant="error">
                                First name is required
                              </FormFeedback>
                            ) : hasFirstNameNumber ? (
                              <FormFeedback variant="error">
                                First name must not contain numbers
                              </FormFeedback>
                            ) : null}
                          </Flex>

                          {/* --- Last Name --- */}
                          <Flex flexDirection="column" flex="1" gap={2}>
                            <FieldLabel
                              label="Last Name"
                              subText=""
                              isRequired
                              helpText={labels.helpText}
                            />

                            <Input
                              {...register(`${mainField}.${index}.lastName`, {
                                validate: value => {
                                  if (!value?.trim()) {
                                    return true;
                                  }
                                  return (
                                    !hasNumber(value) ||
                                    'Last name must not contain numbers'
                                  );
                                }
                              })}
                              placeholder="Enter the reviewer's last name"
                              isInvalid={Boolean(
                                error?.value?.[index]?.lastName ||
                                  hasLastNameNumber
                              )}
                              aria-label="Reviewer last name"
                              onBlur={e => {
                                const trimmed = e.target.value.trim();
                                setValue(
                                  `${mainField}.${index}.lastName`,
                                  trimmed,
                                  {
                                    shouldValidate: true
                                  }
                                );
                              }}
                            />
                            {error?.value?.[index]?.lastName ? (
                              <FormFeedback variant="error">
                                Last name is required
                              </FormFeedback>
                            ) : hasLastNameNumber ? (
                              <FormFeedback variant="error">
                                Last name must not contain numbers
                              </FormFeedback>
                            ) : null}
                          </Flex>
                        </Flex>

                        {/* --- Email --- */}
                        <Flex flexDirection="column" flex="1" gap={2}>
                          <FieldLabel
                            label="Professional Email"
                            subText="Enter the reviewer's professional email address."
                            isRequired
                            helpText={labels.helpText}
                          />

                          <Input
                            type="email"
                            {...register(`${mainField}.${index}.email`, {
                              validate: value => isValidEmail(value)
                            })}
                            aria-label="Reviewer email"
                            placeholder="e.g., fabius@molecularconnections.com"
                            onChange={e => {
                              const email = e.target.value.trim();
                              setValue(`${mainField}.${index}.email`, email, {
                                shouldValidate: true
                              });
                              if (isValidEmail(email)) {
                                validateEmailCOI(email, index);
                              } else {
                                setEmailConflicts(prev => ({
                                  ...prev,
                                  [index]: { hasConflict: false, message: '' }
                                }));
                              }
                            }}
                            isInvalid={
                              emailConflict.hasConflict ||
                              Boolean(error?.value?.[index]?.email)
                            }
                          />
                          {!error?.value?.[index]?.email &&
                          emailConflict.hasConflict ? (
                            <FormFeedback variant="error">
                              {emailConflict.message}
                            </FormFeedback>
                          ) : null}
                          {error?.value?.[index]?.email ? (
                            <FormFeedback variant="error">
                              {error?.value?.[index]?.email.type === 'pattern'
                                ? 'Please enter a valid email'
                                : 'Professional Email is required'}
                            </FormFeedback>
                          ) : null}
                        </Flex>

                        {/* --- Affiliation --- */}
                        <Flex flexDirection="column" flex="1" gap={1}>
                          <FieldLabel
                            label="Affiliated Organisation"
                            subText="Enter the reviewer's current affiliated organisation. Affiliation information is sourced from the Ringgold database."
                            isRequired
                            helpText={labels.helpText}
                          />
                          <RinggoldSearch
                            {...register(`${mainField}.${index}.affiliation`, {
                              required: true
                            })}
                            aria-label="Affiliation"
                            placeholder="Enter an affiliation"
                            isInvalid={Boolean(
                              error?.value?.[index]?.affiliation
                            )}
                            onUpdate={data => {
                              handleOrganizationSelection(data, index);
                              clearErrors(`${mainField}.${index}.affiliation`);
                            }}
                            validateCOI={(affiliation: string) => {
                              const country = watch(
                                `${mainField}.${index}.country`
                              );
                              setValue(
                                `${mainField}.${index}.affiliation`,
                                affiliation,
                                {
                                  shouldValidate: true
                                }
                              );
                              setValue(`${mainField}.${index}.ringgoldId`, '', {
                                shouldValidate: true
                              });
                              validateAffiliationCOI(
                                `${affiliation} ${country}`,
                                index
                              );
                            }}
                            onChange={e => handleRinggoldChange(e, index)}
                            selectedCountry={
                              country ? { country, countryCode } : null
                            }
                            selectedRinggoldId={ringgoldId}
                            error={{
                              affiliation: error?.value?.[index]?.affiliation,
                              country: error?.value?.[index]?.country
                            }}
                          />

                          {!error?.value?.[index]?.affiliation
                            ? affiliationConflict.hasConflict
                              ? affiliationConflict.type === 'error' && (
                                  <FormFeedback variant="error">
                                    {affiliationConflict.message}
                                  </FormFeedback>
                                )
                              : affiliationConflict.type === 'attention' && (
                                  <Text textStyle="textContext" color="#B54708">
                                    <FontAwesomeIcon icon={faInfoCircle} />{' '}
                                    {affiliationConflict.message}
                                  </Text>
                                )
                            : null}
                        </Flex>

                        {/* --- Reason --- */}
                        {reviewTypeName !== 'OpposedReviewers' ? (
                          <Flex flexDirection="column" flex="1" gap={1}>
                            <FieldLabel
                              label="Reason for Recommendation"
                              subText="Provide the reason behind this reviewer's recommendation."
                              isRequired
                              helpText={labels.helpText}
                            />

                            <TextArea
                              value={watch(`${mainField}.${index}.reason`)}
                              {...register(`${mainField}.${index}.reason`)}
                              isInvalid={Boolean(error?.value?.[index]?.reason)}
                              aria-label="Reason"
                            />

                            {error?.value?.[index]?.reason ? (
                              <FormFeedback variant="error">
                                Please enter your reason
                              </FormFeedback>
                            ) : null}
                          </Flex>
                        ) : (
                          <Flex flexDirection="column" flex="1" gap={1}>
                            <FieldLabel
                              label="Reason for opposing reviewer"
                              subText="Provide the reason for opposing this reviewer."
                              isRequired={false}
                              helpText={labels.helpText}
                            />
                            <TextArea
                              value={watch(`${mainField}.${index}.reason`)}
                              {...register(`${mainField}.${index}.reason`, {
                                required: false,
                                minLength: undefined
                              })}
                              aria-label="Reason"
                            />
                          </Flex>
                        )}

                        <ButtonText
                          alignSelf={'flex-end'}
                          iconLeft={<FontAwesomeIcon icon={faTrash} />}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            setEmailConflicts(prev => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                            setAffiliationConflicts(prev => {
                              const updated = { ...prev };
                              delete updated[index];
                              return updated;
                            });
                            remove(index);
                          }}
                        >
                          Delete Reviewer
                        </ButtonText>
                      </Flex>
                    </OutlineCard>
                  );
                })}

                <Flex
                  alignItems="center"
                  justifyContent="space-between" // Keep space-between to allow for alignment
                  width="100%"
                >
                  {error?.value && !Array.isArray(error.value) && (
                    <FormFeedback variant="error">
                      At least one reviewer is required
                    </FormFeedback>
                  )}

                  <ButtonText
                    alignSelf={'flex-start'}
                    iconLeft={<FontAwesomeIcon icon={faPlus} />}
                    onMouseDown={e => e.preventDefault()}
                    onClick={addRow}
                    marginLeft="auto" // Added marginLeft to push the button to the right
                    disabled={
                      config.params.validations.maxItems
                        ? fields.length >= config.params.validations.maxItems
                        : false
                    }
                  >
                    Add {fields.length > 0 ? 'another' : ''} Reviewer
                  </ButtonText>
                </Flex>
              </Flex>
            </OutlineCard>
          </>
        ) : null}
      </Flex>
    </OutlineCard>
  );
};

export default ReviewerRecommendation;
